import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Hook for auto-saving data with debounce
 * @param {Function} saveFn - Async function to save data
 * @param {number} delay - Debounce delay in ms (default 2000)
 * @param {boolean} enabled - Whether auto-save is enabled (default true)
 */
const useAutoSave = (saveFn, delay = 2000, enabled = true) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState(null);
  
  const timeoutRef = useRef(null);
  const saveFnRef = useRef(saveFn);
  const pendingDataRef = useRef(null);
  const saveGenerationRef = useRef(0);
  // Serializes saveNow calls so rapid consecutive saves (e.g. word-boundary
  // saving) cannot finish out of order and overwrite newer data with older.
  const saveQueueRef = useRef(Promise.resolve());

  // Update saveFn ref when it changes
  useEffect(() => {
    saveFnRef.current = saveFn;
  }, [saveFn]);

  // Flush pending changes on unmount (SPA navigation to another page):
  // the request keeps running after unmount since the tab stays open.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (pendingDataRef.current !== null && pendingDataRef.current !== undefined && saveFnRef.current) {
        const data = pendingDataRef.current;
        pendingDataRef.current = null;
        saveFnRef.current(data).catch((err) => {
          console.error('Auto-save on unmount failed:', err);
        });
      }
    };
  }, []);

  // Protect against tab close / refresh with unsaved changes:
  // attempt to save and show the native "leave site?" confirmation.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingDataRef.current !== null && pendingDataRef.current !== undefined) {
        if (saveFnRef.current) {
          // Fire-and-forget; may not complete if the user leaves anyway,
          // hence the confirmation prompt below.
          saveFnRef.current(pendingDataRef.current).catch(() => {});
        }
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /**
   * Trigger auto-save with the given data
   * Will debounce multiple calls within the delay period
   */
  const triggerSave = useCallback((data) => {
    if (!enabled) return;

    setHasUnsavedChanges(true);
    pendingDataRef.current = data;
    saveGenerationRef.current++;
    setError(null);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const generation = saveGenerationRef.current;

    // Set new timeout
    timeoutRef.current = setTimeout(async () => {
      if (!saveFnRef.current || pendingDataRef.current === null || pendingDataRef.current === undefined) return;

      setIsSaving(true);
      try {
        await saveFnRef.current(pendingDataRef.current);
        // Only clear pending state if no newer triggerSave was called during the save
        if (saveGenerationRef.current === generation) {
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
          pendingDataRef.current = null;
        } else {
          setLastSaved(new Date());
        }
      } catch (err) {
        console.error('Auto-save error:', err);
        setError(err);
        toast.error('Auto-save failed. Please save manually.');
      } finally {
        setIsSaving(false);
      }
    }, delay);
  }, [delay, enabled]);

  /**
   * Register pending data without scheduling a timer.
   * The data will be persisted by the next saveNow() call (e.g. on a
   * word boundary or on blur). Used by sections that save on explicit
   * events instead of a debounce timer.
   */
  const markPending = useCallback((data) => {
    if (!enabled) return;
    pendingDataRef.current = data;
    saveGenerationRef.current++;
    setHasUnsavedChanges(true);
    setError(null);
  }, [enabled]);

  /**
   * Force immediate save (bypasses debounce).
   * Calls are queued so they always hit the server in order.
   */
  const saveNow = useCallback(async (data) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const dataToSave = (data !== null && data !== undefined) ? data : pendingDataRef.current;
    if (!saveFnRef.current || dataToSave === null || dataToSave === undefined) return;

    const generation = saveGenerationRef.current;

    const run = async () => {
      setIsSaving(true);
      setError(null);
      try {
        await saveFnRef.current(dataToSave);
        setLastSaved(new Date());
        // Only clear pending state if nothing newer arrived during the save
        if (saveGenerationRef.current === generation) {
          setHasUnsavedChanges(false);
          pendingDataRef.current = null;
        }
      } catch (err) {
        console.error('Save error:', err);
        setError(err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    };

    const chained = saveQueueRef.current.then(run, run);
    saveQueueRef.current = chained.catch(() => {});
    return chained;
  }, []);

  /**
   * Cancel pending auto-save
   */
  const cancelSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingDataRef.current = null;
    setHasUnsavedChanges(false);
  }, []);

  /**
   * Format last saved time for display
   */
  const getLastSavedText = useCallback(() => {
    if (!lastSaved) return null;
    
    const now = new Date();
    const diff = Math.floor((now - lastSaved) / 1000);
    
    if (diff < 5) return 'Just saved';
    if (diff < 60) return `Saved ${diff}s ago`;
    if (diff < 3600) return `Saved ${Math.floor(diff / 60)}m ago`;
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  }, [lastSaved]);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    error,
    triggerSave,
    markPending,
    saveNow,
    cancelSave,
    getLastSavedText,
  };
};

export default useAutoSave;

